/* ── Scoreboard shapes and the match-day math, all as functions of content ──
 *
 * These used to be module-level constants derived from a hand-typed LINEUP.
 * Now the lineup arrives with the managed content, so every derivation takes
 * the content it should size itself off. Pure functions, no React.
 */

import type { Line, Match, TeamContent } from './content';
import type { RosterEntry } from './roster';

/* ── Scores ────────────────────────────────────────────────────────────── */

export interface SetScore {
  us: number | null;
  them: number | null;
}

export interface ScoreData {
  version: 1;
  /** Which match these numbers belong to. The blob is reused week to week,
      so anything stamped for a previous one is treated as empty. */
  season: string;
  updatedAt: string;
  lines: SetScore[][];
}

export function emptyScores(content: Pick<TeamContent, 'seasonKey' | 'lineup' | 'setsPerLine'>): ScoreData {
  return {
    version: 1,
    season: content.seasonKey,
    updatedAt: '',
    lines: content.lineup.map(() =>
      Array.from({ length: content.setsPerLine }, () => ({ us: null, them: null })),
    ),
  };
}

/** Never trust what comes back from storage — coerce it into shape. */
export function sanitizeScores(
  raw: unknown,
  content: Pick<TeamContent, 'seasonKey' | 'lineup' | 'setsPerLine'>,
): ScoreData {
  const data = emptyScores(content);
  if (typeof raw !== 'object' || raw === null) return data;
  // Last week's scores sit in the blob until someone posts over them; an
  // unstamped or stale payload reads as a fresh slate.
  if ((raw as { season?: unknown }).season !== content.seasonKey) return data;
  const updatedAt = (raw as { updatedAt?: unknown }).updatedAt;
  if (typeof updatedAt === 'string') data.updatedAt = updatedAt.slice(0, 40);
  const lines = (raw as { lines?: unknown }).lines;
  if (!Array.isArray(lines)) return data;

  lines.slice(0, content.lineup.length).forEach((line, i) => {
    if (!Array.isArray(line)) return;
    line.slice(0, content.setsPerLine).forEach((set, j) => {
      const us = (set as SetScore)?.us;
      const them = (set as SetScore)?.them;
      data.lines[i][j] = {
        us: typeof us === 'number' && us >= 0 && us <= 99 ? Math.round(us) : null,
        them: typeof them === 'number' && them >= 0 && them <= 99 ? Math.round(them) : null,
      };
    });
  });
  return data;
}

export function setWinner(set: SetScore): 'us' | 'them' | null {
  if (set.us === null || set.them === null || set.us === set.them) return null;
  return set.us > set.them ? 'us' : 'them';
}

export function lineResult(sets: SetScore[]): { us: number; them: number; winner: 'us' | 'them' | null } {
  let us = 0;
  let them = 0;
  for (const set of sets) {
    const winner = setWinner(set);
    if (winner === 'us') us++;
    if (winner === 'them') them++;
  }
  return { us, them, winner: us >= 2 ? 'us' : them >= 2 ? 'them' : null };
}

/* ── Lineup derivations ────────────────────────────────────────────────── */

export type SlotTone = 'done' | 'lost' | 'now';

/** Scoreboard badge per line: the court time, until there is a result to show. */
export function lineSlots(lineup: Line[]): Map<string, { label: string; tone: SlotTone }> {
  const slots = new Map<string, { label: string; tone: SlotTone }>();
  for (const line of lineup) if (line.time) slots.set(line.id, { label: line.time, tone: 'now' });
  return slots;
}

/** Court times, earliest first, each with the lines that go on then. */
export function courtTimes(lineup: Line[]): { time: string; lines: string[] }[] {
  return [...new Set(lineup.map((l) => l.time).filter((t): t is string => Boolean(t)))].map((time) => ({
    time,
    lines: lineup.filter((l) => l.time === time).map((l) => l.id),
  }));
}

/** Everyone the captain actually put on a court this week. */
export function lineupNames(lineup: Line[]): Set<string> {
  return new Set(lineup.flatMap((l) => l.players ?? []).map((p) => p.name));
}

/* ── Schedule ──────────────────────────────────────────────────────────── */

/** Local calendar day as an ISO string — avoids the UTC shift toDateString would hide. */
export function todayIso(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** The next match still to be played, or undefined once the season is done. */
export function upcomingMatch(schedule: Match[], now: Date): Match | undefined {
  const today = todayIso(now);
  // A rained-out match stays up until its last makeup day.
  return schedule.find((m) => !m.bye && !m.result && (m.through ?? m.iso) >= today);
}

/** Whole days from today until an ISO day; negative once it is past. */
export function daysUntil(iso: string, now: Date): number {
  const [y, m, d] = iso.split('-').map(Number);
  const target = new Date(y, m - 1, d);
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((target.getTime() - start.getTime()) / 86_400_000);
}

/** "Seven Hills" → "SH", "Creekside Golf & CC" → "CGCC". */
export function shortName(name: string): string {
  return name
    .split(/\s+/)
    .filter((w) => /^[A-Za-z]/.test(w))
    .map((w) => w[0].toUpperCase())
    .join('')
    .slice(0, 5);
}

/* ── The email digest ──────────────────────────────────────────────────── */

/** The scoreboard as the email renders it: everything the server needs to
 *  draw the same rows the page shows, and nothing it has to look up. */
export interface MatchDigest {
  week: number | null;
  opponent: string;
  opponentShort: string;
  away: boolean;
  label: string;
  season: string;
  club: string;
  lines: {
    id: string;
    players: string[];
    slot: string;
    tone: SlotTone | 'tbd';
    sets: SetScore[];
    winner: 'us' | 'them' | null;
  }[];
}

export function matchDigest(
  data: ScoreData,
  match: Match | undefined,
  content: Pick<TeamContent, 'season' | 'lineup'>,
): MatchDigest {
  const slots = lineSlots(content.lineup);
  return {
    week: match?.week ?? null,
    opponent: match?.opponent ?? 'Opponent',
    opponentShort: shortName(match?.opponent ?? 'Opponent'),
    away: Boolean(match?.away),
    label: match?.label ?? '',
    season: content.season.name,
    club: content.season.club,
    lines: content.lineup.map((line, i) => ({
      id: line.id,
      players: (line.players ?? []).map((p) => p.name),
      slot: slots.get(line.id)?.label ?? '',
      tone: slots.get(line.id)?.tone ?? 'tbd',
      sets: data.lines[i] ?? [],
      winner: lineResult(data.lines[i] ?? []).winner,
    })),
  };
}

/* ── Availability ──────────────────────────────────────────────────────── */

/**
 * The Captain and Co-Captain never appear on the Hall of Shame or in the
 * "still waiting on you" count. They build the lineup, so they already know
 * whether they are playing — an empty availability box is not a lapse for
 * them the way it is for everyone else.
 */
export const isSkipper = (p: RosterEntry) => p.role === 'Captain' || p.role === 'Co-Captain';

export interface RosterGroups {
  available: RosterEntry[];
  unavailable: RosterEntry[];
  maybes: RosterEntry[];
  silent: RosterEntry[];
  /** Everyone who gave a straight answer either way — the people to thank. */
  responded: RosterEntry[];
  /**
   * Nobody who has given a yes or a no. That includes the "maybe" crowd, who
   * clicked something without committing to anything, so they are inducted
   * under a gentler heading than the total no-shows.
   */
  hallOfShame: RosterEntry[];
  total: number;
}

/** `playing` is everyone already on a court this week: he is playing at first
 *  serve whatever his availability box says, so he is not inducted either. */
export function groupRoster(roster: RosterEntry[], playing: Set<string>): RosterGroups {
  const available = roster.filter((p) => p.status === 'in');
  const unavailable = roster.filter((p) => p.status === 'out');
  const maybes = roster.filter((p) => p.status === 'maybe');
  const silent = roster.filter((p) => p.status === 'silent');
  return {
    available,
    unavailable,
    maybes,
    silent,
    responded: [...available, ...unavailable],
    hallOfShame: [...silent, ...maybes].filter((p) => !isSkipper(p) && !playing.has(p.name)),
    total: roster.length,
  };
}
